import EthereumTransaction from 'decentralized/transactions/EthereumTransaction.js';
import BackendQuery from 'decentralized/BackendQuery.js';
import {contracts, SEQUENCE_METADATA} from 'decentralized/transactions/contracts.js';
import {ipfsHashToBytes32, convertAsciiToContractHex} from 'utils/ipfsHash.js';
import {
    SequenceFavorited,
    SequenceTagged,
    SequenceUnfavorited,
    SequenceStarred,
    SequenceUnstarred,
} from 'decentralized/EthereumEvents.js';

const contractAddress = contracts.SEQUENCE_METADATA.options.address;
const favoritesCache = {};
const starsCache = {};
const tagsCache = {};

class SequenceMetadata {

    async tagSequence(id, tag, guildId=0) {
        return new EthereumTransaction(
            SEQUENCE_METADATA,
            'tagSequence',
            [
                ipfsHashToBytes32(id),
                convertAsciiToContractHex(tag),
                guildId
            ])
            .execute();
    }

    async starSequence(id, guildId=0) {
        return new EthereumTransaction(
            SEQUENCE_METADATA,
            'starSequence',
            [
                ipfsHashToBytes32(id),
                guildId
            ])
            .execute();
    }

    async favoriteSequence(id, guildId=0) {
        return new EthereumTransaction(
            SEQUENCE_METADATA,
            'favoriteSequence',
            [
                ipfsHashToBytes32(id),
                guildId
            ])
            .execute();
    }

    async unstarSequence(id, guildId=0) {
        return new EthereumTransaction(
            SEQUENCE_METADATA,
            'unstarSequence',
            [
                ipfsHashToBytes32(id),
                guildId
            ])
            .execute();

    }

    async unfavoriteSequence(id, guildId=0) {
        return new EthereumTransaction(
            SEQUENCE_METADATA,
            'unfavoriteSequence',
            [
                ipfsHashToBytes32(id),
                guildId
            ])
            .execute();
    }

    async isSequenceFavorited(id, user) {
        user = user.toLowerCase();
        let key = id + user;
        if (key in favoritesCache) {
            return favoritesCache[key];
        }
        let favorites = await new BackendQuery(contractAddress)
            .from(SequenceFavorited)
            .select('ipfsHash')
            .where('ipfsHash', id)
            .where('user', user)
            .executeQuery();

        let unfavorites = await new BackendQuery(contractAddress)
            .from(SequenceUnfavorited)
            .select('ipfsHash')
            .where('ipfsHash', id)
            .where('user', user)
            .executeQuery();

        let numFavorites = favorites.length - unfavorites.length;
        favoritesCache[key] = numFavorites > 0;
        return favoritesCache[key];
    }

    async isSequenceStarred(id) {
        if (id in starsCache) {
            return starsCache[id];
        }
        let starred = await new BackendQuery(contractAddress)
            .from(SequenceStarred)
            .select('ipfsHash')
            .where('ipfsHash', id)
            .executeQuery();

        let unstarred = await new BackendQuery(contractAddress)
            .from(SequenceUnstarred)
            .select('ipfsHash')
            .where('ipfsHash', id)
            .executeQuery();

        starsCache[id] = starred.length - unstarred.length > 0;
        return starsCache[id];
    }

    async getTags(id) {
        let tags = await new BackendQuery(contractAddress)
            .from(SequenceTagged)
            .select('tag')
            .where('ipfsHash', id)
            .executeQuery();

        return tags.map(x => x.tag);
    }

    async getStarredSequences(guildId=0) {
        let starredSequences = await new BackendQuery(contractAddress)
            .from(SequenceStarred)
            .select('ipfsHash')
            .where('guildId', 0)
            .executeQuery();

        let unstarredSequences = await new BackendQuery(contractAddress)
            .from(SequenceUnstarred)
            .select('ipfsHash')
            .where('guildId', 0)
            .executeQuery();

        let idToCount = {};
        for (let i in starredSequences) {
            let s  = starredSequences[i];
            if (!(s.ipfsHash in idToCount)) {
                idToCount[s.ipfsHash] = 0;
            }

            idToCount[s.ipfsHash]++;
        }

        for (let i in unstarredSequences) {
            let s  = unstarredSequences[i];
            if (!(s.ipfsHash in idToCount)) {
                idToCount[s.ipfsHash] = 0;
            }

            idToCount[s.ipfsHash]--;
        }

        return Object.keys(idToCount)
            .filter(id => idToCount[id] > 0);
    }

    async getFavoritedSequences(user, guildId=0) {
        user = user.toLowerCase();
        let favoritedSequences = await new BackendQuery(contractAddress)
            .from(SequenceFavorited)
            .select('ipfsHash')
            .where('user', user)
            .where('guildId', guildId)
            .executeQuery();

        let unfavoritedSequences = await new BackendQuery(contractAddress)
            .from(SequenceUnfavorited)
            .select('ipfsHash')
            .where('user', user)
            .executeQuery();

        let idToCount = {};
        for (let i in favoritedSequences) {
            let s  = favoritedSequences[i];
            if (!(s.ipfsHash in idToCount)) {
                idToCount[s.ipfsHash] = 0;
            }

            idToCount[s.ipfsHash]++;
        }

        for (let i in unfavoritedSequences) {
            let s  = unfavoritedSequences[i];
            if (!(s.ipfsHash in idToCount)) {
                idToCount[s.ipfsHash] = 0;
            }

            idToCount[s.ipfsHash]--;
        }

        return Object.keys(idToCount)
            .filter(id => idToCount[id] > 0);
    }

    async getFavorites(id) {
        let favorites = await new BackendQuery(contractAddress)
            .from(SequenceFavorited)
            .select('ipfsHash')
            .select('user')
            .where('ipfsHash', id)
            .executeQuery();

        let unfavorites = await new BackendQuery(contractAddress)
            .from(SequenceUnfavorited)
            .select('ipfsHash')
            .select('user')
            .where('ipfsHash', id)
            .executeQuery();

        let userToCount = {};
        let userToFavorites = {};
        for (let i in favorites) {
            let fav = favorites[i]
            let user = favorites[i].user;
            if (! (user in userToCount)) {
                userToCount[user] = 0;
                userToFavorites[user] = [];
            }

            userToCount[user]++;
            userToFavorites[user].push(fav);
        }

        for (let i in unfavorites) {
            let unfav = unfavorites[i]
            let user = unfavorites[i].user;
            if (! (user in userToCount)) {
                userToCount[user] = 0;
            }

            userToCount[user]--;
        }

        let latest = [];
        for (let user in userToCount) {
            if (userToCount[user] <= 0) {
                continue;
            }

            let fav = userToFavorites[user];
            latest.push(fav[fav.length - 1]);
        }

        return latest;
    }
}

const sequenceMetadata = new SequenceMetadata();
export default sequenceMetadata;
